/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import { useCallback, useMemo } from 'react';

import isFunction from 'lodash/isFunction';
import moment from 'moment';

import { appHistoryPush } from '@/main/modules/app/appHistory';
import { EFormatData } from '@/main/pages/PageDashboard/types/widgetTypes';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import { IApiError } from '@/main/types/globalTypes';

import { ENewsFormCreationFields, ERecipientTypes } from '../../../constants';
import { usePublishInternalNewsItemMutation } from '../../../store';
import { ICreateInternalNewsForm } from '../../../types';

export const useInternalNewsSubmit = (): [
    (
        data: ICreateInternalNewsForm,
        selectedRecipientType: ERecipientTypes,
        resetForm?: () => void,
        setSubmitting?: (isSubmitting: boolean) => void,
    ) => void,
    { isLoading: boolean; isError: boolean; error: IApiError },
] => {
    const [publishInternalNews, { isLoading, isError, error }] = usePublishInternalNewsItemMutation();

    const publish = useCallback(
        (
            data: ICreateInternalNewsForm,
            selectedRecipientType: ERecipientTypes,
            resetForm?: () => void,
            setSubmitting?: (isSubmitting: boolean) => void,
        ) => {
            publishInternalNews({
                title: data[ENewsFormCreationFields.TITLE],
                content: data[ENewsFormCreationFields.MARKDOWN],
                imageLinks: data[ENewsFormCreationFields.IMAGES],
                publishFrom: moment(data[ENewsFormCreationFields.PUBLISH_FROM]).format(EFormatData.FullData),
                publishTo: data[ENewsFormCreationFields.PUBLISH_TO]
                    ? moment(data[ENewsFormCreationFields.PUBLISH_TO]).format(EFormatData.FullData)
                    : undefined,
                secondNotificationAfter: Number(data[ENewsFormCreationFields.REPEAT]) || undefined,
                recipientType: data[ENewsFormCreationFields.USER_FILE] ? selectedRecipientType : ERecipientTypes.ALL,
                file: data[ENewsFormCreationFields.USER_FILE] || undefined,
            })
                .unwrap()
                .then(() => {
                    appHistoryPush(RoutePath.news);
                    if (isFunction(resetForm)) resetForm();
                })
                .catch(() => {
                    if (isFunction(setSubmitting)) setSubmitting(false);
                });
        },
        [publishInternalNews],
    );

    return useMemo(
        () => [
            publish,
            {
                isLoading,
                isError,
                error,
            },
        ],
        [error, isError, isLoading, publish],
    );
};
